import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ShieldCheck, CalendarDays, AlertTriangle, CheckCircle2, Loader2,
  FileText, Receipt, Building2, RefreshCw,
} from 'lucide-react';
import { portal, API_BASE } from '../api';

const portalLogoUrl = (token) => `${API_BASE}/portal/${token}/logo`;
import { YearCalendar, ActivityFeed, UploadsPanel, EffortStats } from '../components/portal/PortalMore';
import { PortalDocuments, PortalApprovals } from '../components/portal/PortalExtras';
import { PortalRequests } from '../components/portal/PortalRequests';

const money = (n, currency = 'ZAR') => {
  const sym = currency === 'ZAR' ? 'R ' : `${currency} `;
  return sym + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const CATEGORY_BAR = {
  'VAT': 'border-l-emerald-500 bg-emerald-50 text-emerald-900',
  'PAYE': 'border-l-blue-500 bg-blue-50 text-blue-900',
  'CIPC': 'border-l-purple-500 bg-purple-50 text-purple-900',
  'Beneficial Ownership': 'border-l-fuchsia-500 bg-fuchsia-50 text-fuchsia-900',
  'Taxation': 'border-l-amber-500 bg-amber-50 text-amber-900',
  'Provisional Tax': 'border-l-orange-500 bg-orange-50 text-orange-900',
  'Bookkeeping': 'border-l-teal-500 bg-teal-50 text-teal-900',
  'Audit': 'border-l-red-500 bg-red-50 text-red-900',
  'Advisory': 'border-l-indigo-500 bg-indigo-50 text-indigo-900',
  'Other': 'border-l-stone-400 bg-stone-50 text-stone-800',
};

const STATUS_PILL = {
  'To Do': 'bg-stone-100 text-stone-700',
  'In Progress': 'bg-sky-50 text-sky-800',
  'Review': 'bg-amber-50 text-amber-800',
  'Done': 'bg-emerald-50 text-emerald-800',
  'Draft': 'bg-stone-100 text-stone-700',
  'Sent': 'bg-sky-50 text-sky-800',
  'Paid': 'bg-emerald-50 text-emerald-800',
};

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('deadlines');
  const [extras, setExtras] = useState({ documents: [], signoffs: [] });
  const loadExtras = () => portal.extras(token).then(setExtras).catch(() => {});

  const load = async () => {
    setLoading(true);
    setError(null);
    loadExtras();
    try {
      const d = await portal.get(token);
      setData(d);
    } catch (err) {
      setError(err?.response?.status === 404 ? 'This link is invalid or has expired. Please contact your accountant for a fresh link.' : 'Could not load the portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading your portal...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center px-4">
        <div className="max-w-md w-full rounded-2xl bg-white border border-stone-200 p-8 text-center shadow-sm">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-50 grid place-items-center text-red-600"><AlertTriangle size={20}/></div>
          <div className="mt-4 font-serif text-xl font-semibold text-stone-900">Link unavailable</div>
          <div className="mt-2 text-sm text-stone-600">{error}</div>
        </div>
      </div>
    );
  }

  const { client, firm, summary, tasks, invoices, generatedAt } = data;
  const openTasks = tasks.filter((t) => t.status !== 'Done');
  const completedTasks = tasks.filter((t) => t.status === 'Done');
  const docsNeeded = (extras.documents[0]?.items || []).filter((i) => i.status !== 'received').length;
  const pendingApprovals = extras.signoffs.filter((s) => s.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f4efe4] to-[#fdfaf3]">
      {/* Header */}
      <header className="bg-gradient-to-br from-emerald-900 via-sky-900 to-emerald-950 text-white relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl"/>
        <div className="pointer-events-none absolute -bottom-24 left-0 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl"/>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-black overflow-hidden grid place-items-center ring-1 ring-sky-500/40">
              <img src={firm.hasLogo ? portalLogoUrl(token) : '/brand/accu-desk-logo.png'} alt={firm.hasLogo ? firm.name : 'Accu Desk'} className="h-full w-full object-contain" data-testid="portal-firm-logo"/>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-sky-200">Client Portal</div>
              <div className="font-serif text-lg font-semibold" data-testid="portal-brand-name">{firm.hasLogo ? firm.name : 'Accu Desk'}</div>
            </div>
            <div className="ml-auto flex items-center gap-2 text-xs text-sky-100/80">
              <ShieldCheck size={14}/> Secure link
            </div>
          </div>
          <div className="mt-8 grid lg:grid-cols-[1fr_auto] items-end gap-6">
            <div>
              <div className="text-sm text-sky-200/90">Welcome,</div>
              <h1 className="mt-1 font-serif text-3xl sm:text-4xl font-semibold leading-tight" data-testid="portal-client-name">{client.name}</h1>
              <div className="mt-1 text-sm text-sky-100/80 flex flex-wrap items-center gap-x-4 gap-y-1">
                {client.entityType && <span className="inline-flex items-center gap-1.5"><Building2 size={13}/> {client.entityType}</span>}
                {client.registrationNumber && <span>Reg #: {client.registrationNumber}</span>}
                {client.taxNumber && <span>Tax #: {client.taxNumber}</span>}
                {client.vatNumber && <span>VAT #: {client.vatNumber}</span>}
              </div>
            </div>
            <div className="text-xs text-sky-100/80 flex items-center gap-2">
              <button onClick={load} className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 bg-white/10 hover:bg-white/20 transition-colors" data-testid="portal-refresh-btn">
                <RefreshCw size={12}/> Refresh
              </button>
              <span>Managed by <span className="font-semibold text-white">{firm.name}</span></span>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="portal-kpis">
          <KPI icon={CalendarDays} label="Open filings" value={summary.openTasks} tone="default" testid="kpi-open"/>
          <KPI icon={AlertTriangle} label="Overdue" value={summary.overdueTasks} tone={summary.overdueTasks > 0 ? 'warn' : 'default'} testid="kpi-overdue"/>
          <KPI icon={Receipt} label="Outstanding" value={money(summary.outstandingAmount)} sub={`${summary.outstandingInvoices} invoice${summary.outstandingInvoices === 1 ? '' : 's'}`} tone={summary.outstandingAmount > 0 ? 'warn' : 'default'} testid="kpi-outstanding"/>
          <KPI icon={CheckCircle2} label="Paid to date" value={money(summary.paidAmount)} tone="good" testid="kpi-paid"/>
        </div>

        <EffortStats token={token}/>

        {/* Tabs */}
        <div className="flex max-w-full overflow-x-auto rounded-lg border border-stone-200 bg-white p-1 sm:inline-flex" data-testid="portal-tabs">
          {[
            { key: 'deadlines', label: 'SARS & CIPC deadlines', count: openTasks.length },
            { key: 'requests', label: 'Ask us' },
            { key: 'calendar', label: 'Year planner' },
            { key: 'activity', label: 'Our work' },
            { key: 'uploads', label: 'Upload documents' },
            { key: 'documents', label: 'Documents', count: docsNeeded },
            { key: 'approvals', label: 'Approvals', count: pendingApprovals },
            { key: 'invoices', label: 'Invoices', count: invoices.length },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`shrink-0 whitespace-nowrap px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-emerald-800 text-white' : 'text-stone-600 hover:text-stone-900'}`}
            >
              {t.label}
              <span className={`ml-2 text-xs ${tab === t.key ? 'text-emerald-100' : 'text-stone-400'}`}>{t.count}</span>
            </button>
          ))}
        </div>

        {tab === 'deadlines' && (
          <section className="space-y-6" data-testid="section-deadlines">
            <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
              <div className="p-5 border-b border-stone-100">
                <div className="font-semibold text-stone-900 flex items-center gap-2"><FileText size={16} className="text-emerald-800"/> Outstanding filings & tasks</div>
                <div className="text-xs text-stone-500 mt-0.5">Everything your accountant is working on for you, live.</div>
              </div>
              <div className="divide-y divide-stone-100">
                {openTasks.length === 0 && (
                  <div className="p-10 text-center text-sm text-stone-500">
                    <CheckCircle2 size={22} className="mx-auto text-emerald-500 mb-2"/>
                    You're all caught up — nothing outstanding right now.
                  </div>
                )}
                {openTasks.map((t) => {
                  const cls = CATEGORY_BAR[t.category] || CATEGORY_BAR['Other'];
                  return (
                    <div key={t.id} className={`p-4 lg:p-5 border-l-4 ${cls.split(' ')[0]} flex flex-col sm:flex-row sm:items-center gap-3`} data-testid={`portal-task-${t.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-900">{t.title}</div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${cls}`}>{t.category}</span>
                          <span className={`px-2 py-0.5 rounded-full font-medium ${STATUS_PILL[t.status] || STATUS_PILL['To Do']}`}>{t.status}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        {t.dueDate ? (
                          <>
                            <div className={`text-sm font-semibold ${t.overdue ? 'text-red-700' : t.daysLeft <= 3 ? 'text-amber-700' : 'text-stone-900'}`}>
                              {new Date(t.dueDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                            <div className="text-[11px] text-stone-500">
                              {t.overdue
                                ? `${Math.abs(t.daysLeft)} day${Math.abs(t.daysLeft) === 1 ? '' : 's'} overdue`
                                : t.daysLeft === 0
                                  ? 'due today'
                                  : `in ${t.daysLeft} day${t.daysLeft === 1 ? '' : 's'}`}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-stone-400">No date set</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {completedTasks.length > 0 && (
              <details className="rounded-xl bg-white border border-stone-200 overflow-hidden">
                <summary className="p-5 cursor-pointer text-sm font-medium text-stone-700 hover:bg-stone-50">
                  <span className="inline-flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600"/> Recently completed <span className="text-stone-400">· {completedTasks.length}</span></span>
                </summary>
                <div className="divide-y divide-stone-100">
                  {completedTasks.slice(0, 15).map((t) => (
                    <div key={t.id} className="p-3 px-5 text-sm flex items-center justify-between">
                      <span className="text-stone-600"><span className="line-through">{t.title}</span> <span className="text-xs text-stone-400 ml-1">{t.category}</span></span>
                      {t.dueDate && <span className="text-xs text-stone-400">{new Date(t.dueDate).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}
          </section>
        )}

        {tab === 'calendar' && <YearCalendar token={token}/>}
        {tab === 'requests' && <PortalRequests token={token}/>}
        {tab === 'activity' && <ActivityFeed token={token}/>}
        {tab === 'uploads' && <UploadsPanel token={token}/>}
        {tab === 'documents' && <PortalDocuments token={token} requests={extras.documents} onChange={loadExtras}/>}
        {tab === 'approvals' && <PortalApprovals token={token} signoffs={extras.signoffs} onChange={loadExtras}/>}

        {tab === 'invoices' && (
          <section data-testid="section-invoices">
            <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
              <div className="p-5 border-b border-stone-100">
                <div className="font-semibold text-stone-900 flex items-center gap-2"><Receipt size={16} className="text-emerald-800"/> Your invoices</div>
                <div className="text-xs text-stone-500 mt-0.5">All invoices issued to {client.name}, including the monthly fee.</div>
              </div>
              {invoices.some((i) => i.balance > 0.004) && (
                <div className="p-5 border-b border-stone-100 bg-amber-50/40" data-testid="portal-owing-breakdown">
                  <div className="flex items-baseline justify-between">
                    <div className="text-sm font-semibold text-stone-900">Amount owing</div>
                    <div className="text-xl font-semibold tabular-nums text-red-700" data-testid="portal-owing-total">{money(summary.outstandingAmount)}</div>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-sm">
                    {invoices.filter((i) => i.balance > 0.004).map((i) => (
                      <li key={i.id} className="flex items-center justify-between gap-3" data-testid={`portal-owing-${i.id}`}>
                        <span className="text-stone-700">{i.label} <span className="text-stone-400">· {i.number}</span>{i.overdue && <span className="ml-2 text-[11px] font-semibold text-red-700 uppercase">overdue</span>}</span>
                        <span className="tabular-nums font-medium text-stone-900">{money(i.balance, i.currency)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {invoices.length === 0 ? (
                <div className="p-10 text-center text-sm text-stone-500">No invoices yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-5 py-3 font-medium">Invoice</th>
                        <th className="text-left px-5 py-3 font-medium">Issued</th>
                        <th className="text-left px-5 py-3 font-medium">Description</th>
                        <th className="text-right px-5 py-3 font-medium">Amount</th>
                        <th className="text-right px-5 py-3 font-medium">Owing</th>
                        <th className="text-left px-5 py-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {invoices.map((i) => (
                        <tr key={i.id} className="hover:bg-stone-50" data-testid={`portal-invoice-${i.id}`}>
                          <td className="px-5 py-4 font-medium text-stone-900">{i.number}</td>
                          <td className="px-5 py-4 text-stone-700">{i.createdAt ? new Date(i.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                          <td className="px-5 py-4 text-stone-700">{i.label}</td>
                          <td className="px-5 py-4 text-right tabular-nums font-semibold text-stone-900">{money(i.total, i.currency)}</td>
                          <td className={`px-5 py-4 text-right tabular-nums ${i.balance > 0.004 ? 'text-red-700 font-semibold' : 'text-stone-400'}`}>{i.balance > 0.004 ? money(i.balance, i.currency) : '—'}</td>
                          <td className="px-5 py-4">
                            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_PILL[i.status] || STATUS_PILL['Draft']}`}>{i.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}

        <div className="text-center text-xs text-stone-500 pt-6">
          Portal generated {new Date(generatedAt).toLocaleString()} · Powered by <span className="font-semibold text-stone-700">Accu Desk</span>
        </div>
      </main>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, tone, testid }) {
  const toneCls = tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-stone-900';
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><Icon size={14}/> {label}</div>
      <div className={`mt-2 text-2xl lg:text-3xl font-serif font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-stone-500">{sub}</div>}
    </div>
  );
}
